<template>
  <div class="wrap">
    <div style="display: flex; justify-content: flex-end; align-items: center">
      <div class="legend-icon" style="background-color: #f56c6c"></div>
      异常值
      <div class="legend-icon" style="background-color: #909399"></div>
      正常值
    </div>

    <div ref="chartRef" class="chart"></div>
    <div
      class="popover-anchor"
      ref="popoverRef"
      :style="{ left: menuX + 'px', top: menuY + 'px' }"
    />
    <el-popover
      v-model:visible="popoverVisible"
      :virtual-ref="popoverRef"
      virtual-triggering
      trigger="manual"
      placement="right-start"
      width="140"
      :show-arrow="false"
    >
      <div class="menu-item danger" @click="removeFromSelection">删除</div>
    </el-popover>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  nextTick,
  onBeforeUnmount,
  defineExpose,
  defineEmits,
} from "vue";
import * as echarts from "echarts";

const emit = defineEmits(["change"]);

/* ================= refs ================= */
const chartRef = ref(null);
const chart = ref(null);

/* ================= data ================= */
const data = ref([]);
const optionsRef = ref(null);
const popoverRef = ref();
/** 已选点 */
const selectedMap = ref(new Map());

/** 框选临时 index */
const brushTempIndexSet = ref(new Set());

const ignoreNextBrush = ref(false);

/** popover */
const popoverVisible = ref(false);
const menuX = ref(0);
const menuY = ref(0);
const activeTime = ref(null);

/* ================= computed ================= */

const emitAnswer = () => {
  const res = Array.from(selectedMap.value.values()).map((i) => ({
    x: i.time,
    y: i.value,
  }));
  emit("change", res);
};
/* ================= 核心：open ================= */
const open = async (question, answer) => {
  optionsRef.value = JSON.parse(question.problem);

  // 1️⃣ 数据初始化
  data.value = Array.isArray(optionsRef.value.metrics)
    ? optionsRef.value.metrics.map((item) => ({
        time: item.x,
        value: item.y,
      }))
    : [];

  selectedMap.value.clear();
  brushTempIndexSet.value.clear();
  popoverVisible.value = false;

  await nextTick();
  chart.value?.clear();
  initChart(answer);
  await nextTick();
};

/* ================= 初始化 ================= */
const initChart = (answer) => {
  chart.value = echarts.init(chartRef.value);

  if (answer && answer.length > 0) {
    answer.forEach((item) => {
      selectedMap.value.set(item.x, { time: item.x, value: item.y });
    });
  }
  render();
  enableBrush();
  /** 框选实时 */
  chart.value.on("brushSelected", (params) => {
    if (ignoreNextBrush.value) return;

    const batch = params.batch?.[0];
    if (!batch) return;

    brushTempIndexSet.value.clear();

    batch.selected.forEach((item) => {
      if (item.seriesIndex === 1) {
        item.dataIndex.forEach((index) => {
          brushTempIndexSet.value.add(index);
        });
      }
    });
  });

  /** 框选结束 */
  chart.value.on("brushEnd", () => {
    brushTempIndexSet.value.forEach((index) => {
      const d = data.value[index];
      selectedMap.value.set(d.time, d);
    });

    render();

    ignoreNextBrush.value = true;
    clearBrush();

    nextTick(() => {
      ignoreNextBrush.value = false;
    });

    emitAnswer();
  });

  /** 点点击 */
  chart.value.on("click", (params) => {
    popoverVisible.value = false;
    if (params.seriesType !== "scatter") return;

    const d = data.value[params.dataIndex];
    if (!selectedMap.value.has(d.time)) return;

    activeTime.value = d.time;
    menuX.value = params.event.event.clientX;
    menuY.value = params.event.event.clientY;

    requestAnimationFrame(() => {
      popoverVisible.value = true;
    });
  });

  window.addEventListener("click", hidePopover);
};

/* ================= brush ================= */
const enableBrush = () => {
  chart.value.dispatchAction({
    type: "takeGlobalCursor",
    key: "brush",
    brushOption: {
      brushType: "rect",
      brushMode: "multiple",
    },
  });
};

const clearBrush = () => {
  chart.value.dispatchAction({
    type: "brush",
    areas: [],
  });
};

/* ================= 删除 ================= */
const removeFromSelection = () => {
  if (activeTime.value !== null) {
    selectedMap.value.delete(activeTime.value);
  }
  popoverVisible.value = false;
  render();
  emitAnswer();
};

/* ================= 对外 API ================= */
const restoreSelection = (points) => {
  points.forEach((p) => {
    selectedMap.value.set(p.time, p);
  });
  render();
};

const clearSelectedPoints = () => {
  selectedMap.value.clear();
  render();
};

const getSelectedPoints = () => {
  return Array.from(selectedMap.value.values());
};

const hidePopover = () => {
  popoverVisible.value = false;
};

/* ================= render ================= */
const render = () => {
  if (!chart.value || !data.value.length) return;

  const { name, unit } = optionsRef.value || {};

  chart.value.setOption({
    grid: {
      left: 80,
      right: 20,
      top: 20,
      bottom: 80,
    },
    xAxis: {
      type: "category",
      data: data.value.map((d) => d.time),
    },
    toolbox: {
      show: false,
    },
    yAxis: {
      axisLabel: {
        formatter: `{value}${unit || ""}`,
      },
      splitLine: { show: false },
    },
    dataZoom: [{ type: "inside" }, { type: "slider", bottom: 20 }],
    brush: {
      brushType: "rect",
      brushMode: "multiple",
      seriesIndex: [1],
    },
    series: [
      {
        name,
        type: "line",
        data: data.value.map((d) => d.value),
        showSymbol: false,
        z: 1,
      },
      {
        type: "scatter",
        symbolSize: 14,
        z: 10,
        data: data.value.map((d) => ({
          value: d.value,
          itemStyle: {
            color: selectedMap.value.has(d.time) ? "#F56C6C" : "#909399",
          },
        })),
      },
    ],
  });
};

onBeforeUnmount(() => {
  window.removeEventListener("click", hidePopover);
  chart.value?.dispose();
});

/* ================= 暴露 ================= */
defineExpose({
  open,
  restoreSelection,
  clearSelectedPoints,
  getSelectedPoints,
});
</script>

<style lang="less" scoped>
.wrap {
  position: relative;
}
.chart {
  width: 100%;
  height: 500px;
  position: relative;
}

.popover-anchor {
  position: fixed;
  width: 0;
  height: 0;
  pointer-events: none;
  z-index: 10000;
}

.legend-icon {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  margin: 0 8px;
}

.menu-item {
  padding: 8px 12px;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
  }

  &.danger {
    color: #ef4444;
  }
}
</style>
